import type { SerializedPattern } from './serialize';

/**
 * Wrapper IndexedDB minimaliste pour la persistance locale du brouillon.
 *
 * Post-pivot e-commerce (cahier-des-charges-ecommerce-v01.md §3.1, Lot N4) :
 * plus de comptes utilisateurs ni de sync cloud — les champs `ownerUid`,
 * `cloudSyncedAt` et `publishedAs` ont été retirés du record. L'IDB ne sert
 * plus que de filet de sécurité local : le brouillon du configurateur
 * survit au refresh, jusqu'au passage en commande.
 *
 * Les enregistrements hérités (avec champs cloud) restent lisibles : les
 * champs inconnus sont ignorés à la lecture et purgés à la prochaine
 * écriture (freshRecord ne réécrit que les champs connus).
 */

const DB_NAME = 'perleur';
const DB_VERSION = 1;
const STORE_PROJECTS = 'projects';

/** Record stocké en IndexedDB. */
export type ProjectRecord = {
  id: string;
  name: string;
  pattern: SerializedPattern;
  createdAt: number;
  updatedAt: number;
};

/**
 * `indexedDB` disponible ? (faux en SSR, en navigateurs anciens, ou en mode privé
 * trop restrictif). À vérifier avant d'appeler les autres fonctions.
 */
export function isIndexedDbAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    return false;
  }
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (): void => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_PROJECTS)) {
        db.createObjectStore(STORE_PROJECTS, { keyPath: 'id' });
      }
    };
    req.onsuccess = (): void => resolve(req.result);
    req.onerror = (): void => {
      dbPromise = null;
      reject(req.error ?? new Error('IndexedDB open a échoué.'));
    };
    req.onblocked = (): void => {
      reject(new Error('IndexedDB bloquée par une autre instance ouverte.'));
    };
  });
  return dbPromise;
}

function txDone<T>(tx: IDBTransaction, valueGetter?: () => T): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    tx.oncomplete = (): void => resolve(valueGetter ? valueGetter() : (undefined as T));
    tx.onerror = (): void => reject(tx.error ?? new Error('Transaction IndexedDB échouée.'));
    tx.onabort = (): void => reject(tx.error ?? new Error('Transaction IndexedDB abandonnée.'));
  });
}

/**
 * Reconstruit chaque champ en littéral frais avant l'écriture IDB.
 *
 * IndexedDB rejette les Svelte 5 `$state` Proxies avec un DataCloneError.
 * Le record passé peut venir d'un `$state`, donc on normalise ici par
 * sécurité plutôt que de demander à chaque appelant de le faire.
 * Effet secondaire bienvenu : les champs hérités de l'ère communautaire
 * (ownerUid, cloudSyncedAt, publishedAs) ne sont pas recopiés → purge douce.
 */
function freshRecord(record: ProjectRecord): ProjectRecord {
  const p = record.pattern;
  return {
    id: record.id,
    name: record.name,
    pattern: {
      width: p.width,
      height: p.height,
      cells: [...p.cells],
      paletteColorIds: [...p.paletteColorIds],
      counts: { ...p.counts },
      layout: {
        plateWidth: p.layout.plateWidth,
        plateHeight: p.layout.plateHeight,
        cols: p.layout.cols,
        rows: p.layout.rows,
      },
      physicalSize: {
        widthCm: p.physicalSize.widthCm,
        heightCm: p.physicalSize.heightCm,
      },
      estimatedTimeMinutes: p.estimatedTimeMinutes,
    },
    createdAt: record.createdAt,
    updatedAt: record.updatedAt,
  };
}

/** Sauvegarde (ou écrase) un projet. Normalise les Proxies Svelte 5 en
 *  littéraux frais avant l'écriture pour éviter DataCloneError. */
export async function saveProject(record: ProjectRecord): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE_PROJECTS, 'readwrite');
  tx.objectStore(STORE_PROJECTS).put(freshRecord(record));
  await txDone(tx);
}

/** Charge un projet par id. Retourne `null` si absent. Les champs hérités
 *  éventuels (ownerUid…) sont simplement ignorés par le type. */
export async function loadProject(id: string): Promise<ProjectRecord | null> {
  const db = await openDb();
  const tx = db.transaction(STORE_PROJECTS, 'readonly');
  const req = tx.objectStore(STORE_PROJECTS).get(id);
  return new Promise<ProjectRecord | null>((resolve, reject) => {
    req.onsuccess = (): void => {
      const raw = req.result as ProjectRecord | undefined;
      resolve(raw ?? null);
    };
    req.onerror = (): void => reject(req.error ?? new Error('IndexedDB get échoué.'));
  });
}

/**
 * Liste tous les projets en UNE seule transaction IDB (getAll).
 * Sert uniquement à l'adoption du brouillon (cf. draft.ts) : récupérer le
 * projet le plus récent d'une installation antérieure au pivot.
 */
export async function listProjects(): Promise<ProjectRecord[]> {
  const db = await openDb();
  const tx = db.transaction(STORE_PROJECTS, 'readonly');
  const req = tx.objectStore(STORE_PROJECTS).getAll();
  return new Promise<ProjectRecord[]>((resolve, reject) => {
    req.onsuccess = (): void => resolve((req.result as ProjectRecord[]) ?? []);
    req.onerror = (): void => reject(req.error ?? new Error('IndexedDB getAll échoué.'));
  });
}

/** Supprime un projet. No-op si absent. */
export async function deleteProject(id: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE_PROJECTS, 'readwrite');
  tx.objectStore(STORE_PROJECTS).delete(id);
  await txDone(tx);
}
